(ns practitest.frontend.integration-settings.azure-entity-mapping.events
  (:require [re-frame.core :as re-frame]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.components.notifications :as notifications]
            [ajax.core :as ajax]))

(def ^:private default-requirements-types #{"User Story"})
(def ^:private default-issues-types #{"Bug" "Task"})

(defn- apply-defaults
  "Given a flat list of work item types for a project, return a default mapping
   using the standard Azure classification."
  [work-item-types]
  (let [all-ids      (map :id work-item-types)
        req-types    (vec (filter #(contains? default-requirements-types %) all-ids))
        issue-types  (vec (filter #(contains? default-issues-types %) all-ids))]
    {:requirements {:azure_type req-types}
     :issues       {:azure_type issue-types}}))

(defn- build-sp-map
  "Build a {type-id -> supports-parent?} map from a list of work item types."
  [types]
  (into {} (map (fn [t] [(:id t) (boolean (:supports_parent t))]) types)))

(defn- new-project-mapping
  "Build an initial mapping entry for a newly selected project.
   If work item types are already cached, applies defaults immediately.
   Otherwise returns a placeholder with :_pending_defaults? true."
  [work-item-types-map pid-key]
  (let [pid-str (name pid-key)
        types   (get work-item-types-map pid-str)]
    (if types
      (assoc (apply-defaults types) :type_supports_parent (build-sp-map types))
      {:requirements       {:azure_type []}
       :issues             {:azure_type []}
       :_pending_defaults? true})))

;; Serialized lazy-loading of work item types per project (same pattern as JIRA).
(defn- chain-next-fetch [db]
  (let [selected (get-in db [:azure-entity-mapping :selected-project-ids] #{})
        cached   (get-in db [:azure-entity-mapping :project-work-item-types] {})
        loading  (get-in db [:azure-entity-mapping :project-types-loading] #{})
        failed   (get-in db [:azure-entity-mapping :project-types-failed] #{})]
    (when-let [pid (->> selected
                        (map name)
                        (remove #(contains? cached %))
                        (remove #(contains? loading %))
                        (remove #(contains? failed %))
                        first)]
      [[:dispatch [::fetch-project-work-item-types pid]]])))

(re-frame/reg-event-db
 ::initialize-db
 (fn [db [_ {:keys [project-id integration-id integration-name]}]]
   (assoc db :azure-entity-mapping
          {:project-id              project-id
           :integration-id          integration-id
           :integration-name        integration-name
           :loading?                true
           :projects-loading?       true
           :selected-project-ids    #{}
           :available-projects      []
           :project-work-item-types {}
           :project-types-loading   #{}
           :project-types-failed    #{}
           :data                    nil
           :errors                  []})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:azure-entity-mapping db)]
     {:http-xhrio {:method          :get
                   :uri             (broutes/get-url "azure_integration_settings#entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-entity-mapping-success]
                   :on-failure      [::fetch-entity-mapping-failure]}})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-success
 (fn [{:keys [db]} [_ response]]
   (let [project-mappings (:project_mappings response {})
         mapped-pids      (keys project-mappings)
         db'              (update db :azure-entity-mapping merge
                                  {:loading?                   false
                                   :data                       response
                                   :saved-data                 response
                                   :selected-project-ids       (set mapped-pids)
                                   :saved-selected-project-ids (set mapped-pids)
                                   :errors                     []})]
     {:db db'
      :fx (chain-next-fetch db')})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.fetch-failed"
                                                "Failed to load entity mapping"))]
     {:db       (update db :azure-entity-mapping merge {:loading? false})
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-fx
 ::fetch-available-projects
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:azure-entity-mapping db)]
     {:db         (assoc-in db [:azure-entity-mapping :projects-loading?] true)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url "azure_integration_settings#available_azure_projects"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-available-projects-success]
                   :on-failure      [::fetch-available-projects-failure]}})))

(re-frame/reg-event-db
 ::fetch-available-projects-success
 (fn [db [_ response]]
   (update db :azure-entity-mapping merge
           {:projects-loading?  false
            :available-projects (vec (:available_projects response []))})))

(re-frame/reg-event-fx
 ::fetch-available-projects-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (format-translation (get-translation "bugtracker.integration-settings.error.projects-fetch-failed"
                                                                    "Failed to load %{bugtracker} projects")
                                                   :bugtracker "Azure DevOps"))]
     {:db       (assoc-in db [:azure-entity-mapping :projects-loading?] false)
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types
 (fn [{:keys [db]} [_ azure-project-id]]
   (let [{:keys [project-id integration-id]} (:azure-entity-mapping db)
         loading-set     (get-in db [:azure-entity-mapping :project-types-loading] #{})
         types-cache     (get-in db [:azure-entity-mapping :project-work-item-types] {})
         already-active? (or (contains? loading-set azure-project-id)
                             (contains? types-cache azure-project-id))]
     (if already-active?
       {}
       {:db         (update-in db [:azure-entity-mapping :project-types-loading] (fnil conj #{}) azure-project-id)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "azure_integration_settings#entity_mapping_work_item_types"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :params          {:azure_project_id azure-project-id}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-project-work-item-types-success azure-project-id]
                     :on-failure      [::fetch-project-work-item-types-failure azure-project-id]}}))))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types-success
 (fn [{:keys [db]} [_ azure-project-id response]]
   (let [pid-key       (keyword azure-project-id)
         work-item-types (vec response)
         has-mapping?  (some? (get-in db [:azure-entity-mapping :data :project_mappings pid-key]))
         db'           (cond-> db
                         true         (update-in [:azure-entity-mapping :project-work-item-types] assoc azure-project-id work-item-types)
                         true         (update-in [:azure-entity-mapping :project-types-loading] (fnil disj #{}) azure-project-id)
                         ;; Populate defaults for newly-added projects
                         (and has-mapping?
                              (get-in db [:azure-entity-mapping :data :project_mappings pid-key :_pending_defaults?]))
                         (update-in [:azure-entity-mapping :data :project_mappings pid-key]
                                    (fn [m]
                                      (-> (apply-defaults work-item-types)
                                          (assoc :project_name (:project_name m))
                                          (assoc :type_supports_parent (build-sp-map work-item-types))
                                          (dissoc :_pending_defaults?)))))]
     {:db db'
      :fx (chain-next-fetch db')})))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types-failure
 (fn [{:keys [db]} [_ azure-project-id _response]]
   (let [db' (-> db
                 (update-in [:azure-entity-mapping :project-types-loading] (fnil disj #{}) azure-project-id)
                 (update-in [:azure-entity-mapping :project-types-failed] (fnil conj #{}) azure-project-id))]
     {:db db'
      :fx (into [[:dispatch [::notifications/error2
                             (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                  "Failed to load %{bugtracker} types for one of the projects")
                                                :bugtracker "Azure DevOps")]]]
                (chain-next-fetch db'))})))

(re-frame/reg-event-db
 ::toggle-requirements-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:azure-entity-mapping :data :requirements_sync_enabled] enabled?)))

(re-frame/reg-event-db
 ::toggle-issues-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:azure-entity-mapping :data :issues_sync_enabled] enabled?)))

(re-frame/reg-event-fx
 ::update-selected-projects
 (fn [{:keys [db]} [_ selected-project-ids]]
   (let [current-mappings    (get-in db [:azure-entity-mapping :data :project_mappings] {})
         available-projects  (get-in db [:azure-entity-mapping :available-projects] [])
         work-item-types-map (get-in db [:azure-entity-mapping :project-work-item-types] {})
         projects-by-id    (into {} (map (fn [p] [(keyword (:id p)) (:name p)]) available-projects))
         new-mappings
         (reduce
           (fn [acc pid-key]
             (let [mapping (if (contains? current-mappings pid-key)
                             (get current-mappings pid-key)
                             (new-project-mapping work-item-types-map pid-key))
                   p-name  (get projects-by-id pid-key)]
               (cond-> (assoc acc pid-key mapping)
                 p-name (assoc-in [pid-key :project_name] p-name))))
           {}
           selected-project-ids)
         db' (-> db
                 (assoc-in [:azure-entity-mapping :selected-project-ids] selected-project-ids)
                 (assoc-in [:azure-entity-mapping :data :project_mappings] new-mappings))]
     {:db db'
      :fx (when (empty? (get-in db' [:azure-entity-mapping :project-types-loading] #{}))
            (chain-next-fetch db'))})))

(re-frame/reg-event-db
 ::update-project-mapping
 (fn [db [_ project-id entity-type field value]]
   (assoc-in db [:azure-entity-mapping :data :project_mappings
                 (keyword project-id) entity-type field]
             value)))

(re-frame/reg-event-db
 ::toggle-project-entity-status
 (fn [db [_ project-id entity-type]]
   (let [pid-key         (keyword project-id)
         current-mappings (get-in db [:azure-entity-mapping :data :project_mappings] {})
         entity-path     [pid-key entity-type]
         current-entity  (get-in current-mappings entity-path)
         is-enabling?    (:disabled current-entity false)
         work-item-types (get-in db [:azure-entity-mapping :project-work-item-types project-id])
         updated-entity  (if is-enabling?
                           (if work-item-types
                             {:azure_type (vec (filter #(contains? (if (= entity-type :requirements)
                                                                     default-requirements-types
                                                                     default-issues-types) %)
                                                       (map :id work-item-types)))}
                             {:azure_type []})
                           {:azure_type [] :disabled true})]
     (assoc-in db [:azure-entity-mapping :data :project_mappings]
               (assoc-in current-mappings entity-path updated-entity)))))

(re-frame/reg-event-db
 ::toggle-allow-issue-creation
 (fn [db [_ enabled?]]
   (assoc-in db [:azure-entity-mapping :data :allow_issue_creation] enabled?)))

(re-frame/reg-event-db
 ::toggle-use-pt-id
 (fn [db [_ enabled?]]
   (assoc-in db [:azure-entity-mapping :data :use_pt_id] enabled?)))

(re-frame/reg-event-db
 ::toggle-skip-mapping-manual-import
 (fn [db [_ enabled?]]
   (assoc-in db [:azure-entity-mapping :data :skip_mapping_in_manual_import] enabled?)))

(re-frame/reg-event-fx
 ::save-settings
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id data available-projects]} (:azure-entity-mapping db)
         projects-by-id      (into {} (map (fn [p] [(keyword (:id p)) (:name p)]) available-projects))
         work-item-types-map (get-in db [:azure-entity-mapping :project-work-item-types] {})
         enriched-mappings   (reduce-kv
                               (fn [m pid _mapping]
                                 (let [p-name     (get projects-by-id pid)
                                       types-list (get work-item-types-map (name pid) [])
                                       sp-map     (when (seq types-list) (build-sp-map types-list))]
                                   (cond-> m
                                     p-name  (assoc-in [pid :project_name] p-name)
                                     sp-map  (assoc-in [pid :type_supports_parent] sp-map))))
                               (:project_mappings data {})
                               (:project_mappings data {}))
         enriched-data       (assoc data :project_mappings enriched-mappings)]
     {:db (assoc-in db [:azure-entity-mapping :saving?] true)
      :http-xhrio {:method          :put
                   :uri             (broutes/get-url "azure_integration_settings#update_entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :params          enriched-data
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::save-settings-success]
                   :on-failure      [::save-settings-failure]}})))

(re-frame/reg-event-fx
 ::save-settings-success
 (fn [{:keys [db]} [_]]
   (let [current-data     (get-in db [:azure-entity-mapping :data])
         current-selected (get-in db [:azure-entity-mapping :selected-project-ids])]
     {:db       (update db :azure-entity-mapping merge
                        {:saving?                    false
                         :saved-data                 current-data
                         :saved-selected-project-ids current-selected
                         :errors                     []})
      :dispatch [::notifications/success2
                 (get-translation "bugtracker.integration-settings.save.success"
                                  "Settings saved successfully")]})))

(re-frame/reg-event-fx
 ::save-settings-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.save-failed"
                                                "Failed to save entity mapping"))]
     {:db       (assoc-in db [:azure-entity-mapping :saving?] false)
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-db
 ::cancel-changes
 (fn [db [_]]
   (let [saved-data     (get-in db [:azure-entity-mapping :saved-data])
         saved-selected (get-in db [:azure-entity-mapping :saved-selected-project-ids])]
     (-> db
         (assoc-in [:azure-entity-mapping :data] saved-data)
         (assoc-in [:azure-entity-mapping :selected-project-ids] saved-selected)))))
